import React, { useState, useRef, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { RevealPath } from './RevealPath';
import { DocumentViewer, DocumentViewerRef } from './DocumentViewer';
import { ConfigPanel } from './ConfigPanel';
import { RiskPanel } from './RiskPanel';
import { Contract, AnalysisState, PreviewState, ReviewConfig } from '../types';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface AppLayoutProps {
    history: Contract[];
    activeContract: Contract | null;
    setActiveContract: React.Dispatch<React.SetStateAction<Contract | null>>;
    analysisState: AnalysisState;
    viewingHistoryResult: boolean;
    handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleDownload: () => void;
    companyPolicy: string;
    setCompanyPolicy: (val: string) => void;
    selectedClauseIndex: number | null;
    setSelectedClauseIndex: (val: number | null) => void;
    // New: Preview state and config handler
    previewState: PreviewState | null;
    handleStartAnalysis: (config: ReviewConfig) => void;
    // New: Handle reset
    onNewProject: () => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
    history,
    activeContract,
    setActiveContract,
    analysisState,
    viewingHistoryResult,
    handleFileUpload,
    handleDownload,
    companyPolicy,
    setCompanyPolicy,
    selectedClauseIndex,
    setSelectedClauseIndex,
    previewState,
    handleStartAnalysis,
    onNewProject
}) => {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState<'workbench' | 'reveal'>('workbench');
    const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(true);
    const [historySelectKey, setHistorySelectKey] = useState(0);
    const [isPolicyManagerOpen, setIsPolicyManagerOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const documentViewerRef = useRef<DocumentViewerRef>(null);
    const prevActiveIdRef = useRef<string | null>(null);

    // 移除自动切换到 reveal 的逻辑，让用户手动选择标签页

    // Resizable Sidebar Logic
    const [rightPanelWidth, setRightPanelWidth] = useState(380);
    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            // 计算新宽度：屏幕总宽 - 鼠标X坐标
            const newWidth = document.body.clientWidth - e.clientX;
            // 限制宽度范围：最小 280px，最大 800px (防止挤得太小或太大)
            const clampedWidth = Math.max(280, Math.min(newWidth, 800));
            setRightPanelWidth(clampedWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            // 添加 user-select: none 防止拖拽时选中文本
            document.body.style.userSelect = 'none';
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            if (!isResizing) {
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
            }
        };
    }, [isResizing]);

    // Check if we're in preview mode (file uploaded but analysis not started)
    const isPreviewMode = previewState !== null && previewState.isReady;

    return (
        <div
            className="flex flex-col h-screen h-[100dvh] w-screen overflow-hidden font-sans"
            style={{
                backgroundColor: '#1a1a1a'
            }}
        >
            {/* Top Banner */}
            <div 
                className="w-full py-3 flex items-center justify-center"
                style={{ backgroundColor: '#1a1a1a' }}
            >
                <span className="text-white text-sm font-medium">
                    DocSure AI | Legal Intelligence Console
                </span>
            </div>

            {/* Main Content Area */}
            <div 
                className="flex flex-1 overflow-hidden rounded-t-3xl relative"
                style={{ 
                    backgroundColor: '#F5F5F0',
                    backgroundImage: 'url(/功能页背景.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
            {/* 1. Global Left Sidebar */}
            <div 
                className={`relative transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${isSidebarVisible ? "p-4" : "p-4 relative"}`}
                style={{
                    width: isSidebarVisible ? '320px' : '0px',
                    minWidth: isSidebarVisible ? '320px' : '0px',
                    overflow: isSidebarVisible ? 'visible' : 'visible',
                    transition: 'width 700ms cubic-bezier(0.4, 0, 0.2, 1), min-width 700ms cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: isSidebarVisible ? 'auto' : 9999
                }}
            >
                <Sidebar
                    history={history}
                    activeContractId={activeContract?.id}
                    onSelectContract={async (c, opts) => {
                        await setActiveContract(c);
                        if (opts?.fromHistory) {
                            setHistorySelectKey((k) => k + 1);
                            // 不再自动切换到 reveal，保持在 workbench 显示文档
                        }
                    }}
                    onNewProject={onNewProject}
                    companyPolicy={companyPolicy}
                    setCompanyPolicy={setCompanyPolicy}
                    onLogout={() => { }} // Handle logout if needed
                    onBack={() => navigate('/')}
                    isSidebarVisible={isSidebarVisible}
                    onToggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
                    onPolicyManagerToggle={(isOpen) => setIsPolicyManagerOpen(isOpen)}
                />
            </div>

            {/* 2. Global Right Content Area */}
            <div 
                className="flex-1 relative flex flex-col overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                style={{
                    transform: isPolicyManagerOpen ? 'translateX(100px)' : 'translateX(0)'
                }}
            >
                {/* Tab bar - iOS液态玻璃风格，始终显示 */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
                    <div
                        className="relative flex items-center gap-0.5 p-1 rounded-2xl pointer-events-auto"
                        style={{
                            background: 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(40px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                        }}
                    >
                        {/* 选中指示器 - iOS风格 */}
                        <div
                            className="absolute rounded-xl pointer-events-none"
                            style={{
                                top: '4px',
                                bottom: '4px',
                                left: activeTab === 'workbench' ? '4px' : 'calc(50% + 2px)',
                                width: 'calc(50% - 6px)',
                                background: 'rgba(0, 0, 0, 0.65)',
                                backdropFilter: 'blur(20px)',
                                WebkitBackdropFilter: 'blur(20px)',
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                zIndex: 0
                            }}
                        />
                        <button
                            onClick={() => setActiveTab('workbench')}
                            className={`relative z-10 px-7 py-2.5 rounded-xl text-xs font-semibold transition-all duration-300 ${activeTab === 'workbench'
                                ? 'text-white'
                                : 'text-slate-600 hover:text-slate-900'
                                }`}
                            style={{
                                minWidth: '110px',
                                WebkitTapHighlightColor: 'transparent'
                            }}
                        >
                            {t('nav.workbench')}
                        </button>
                        <button
                            onClick={() => setActiveTab('reveal')}
                            className={`relative z-10 px-7 py-2.5 rounded-xl text-xs font-semibold transition-all duration-300 ${activeTab === 'reveal'
                                ? 'text-white'
                                : 'text-slate-600 hover:text-slate-900'
                                }`}
                            style={{
                                minWidth: '110px',
                                WebkitTapHighlightColor: 'transparent'
                            }}
                        >
                            Reveal Path™
                        </button>
                    </div>
                </div>

                {/* Main Content View */}
                <div className="flex-1 w-full h-full relative overflow-hidden">
                    <div
                        className="flex h-full transition-transform duration-500 ease-in-out"
                        style={{
                            transform: activeTab === 'reveal' ? 'translateX(-50%)' : 'translateX(0)',
                            width: '200%'
                        }}
                    >
                        {/* Workbench/Document View - 左侧 */}
                        <div className="w-1/2 flex-shrink-0 h-full">
                            {isPreviewMode || (activeContract && activeContract.status !== 'pending') ? (
                                /* Document Viewer Mode: Read-only document display (left) + Config/Risk Panel (right) */
                                <div className="flex h-full">
                                    {/* Document Viewer - 主内容区域 */}
                                    <div
                                        className="flex-1 h-full relative min-w-0"
                                        onMouseMove={(e) => {
                                            if (!isResizing) {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const isNearEdge = e.clientX >= rect.right - 8;
                                                e.currentTarget.style.cursor = isNearEdge ? 'col-resize' : 'default';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isResizing) {
                                                e.currentTarget.style.cursor = 'default';
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const isNearEdge = e.clientX >= rect.right - 8;
                                            if (isNearEdge) {
                                                e.preventDefault();
                                                setIsResizing(true);
                                            }
                                        }}
                                    >
                                        <DocumentViewer
                                            ref={documentViewerRef}
                                            fileName={previewState?.fileName || activeContract?.name || ''}
                                            content={previewState?.content || activeContract?.fullContent || []}
                                            htmlContent={previewState?.htmlContent || activeContract?.displayHtml}
                                            clauses={activeContract?.clauses || []}
                                            selectedClauseIndex={selectedClauseIndex}
                                            onSelectClause={setSelectedClauseIndex}
                                            isAnalyzing={analysisState.isAnalyzing}
                                            analysisProgress={analysisState.progress}
                                            onDownload={handleDownload}
                                            showDownload={activeContract?.status === 'completed'}
                                        />
                                    </div>

                                    {/* Config Panel (预览模式) 或 Risk Panel (分析后) */}
                                    <div
                                        className="h-full flex-shrink-0"
                                        style={{
                                            width: rightPanelWidth,
                                            transition: isResizing ? 'none' : 'width 0.3s ease-in-out', // 拖拽时禁用过渡，松开后恢复
                                            borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}
                                    >
                                        {isPreviewMode ? (
                                            <ConfigPanel
                                                onStartAnalysis={(config) => {
                                                    handleStartAnalysis(config);
                                                    // 自动切换到reveal path
                                                    setTimeout(() => {
                                                        setActiveTab('reveal');
                                                    }, 100);
                                                }}
                                                isLoading={false}
                                            />
                                        ) : (
                                            /* 分析完成后显示风险面板 */
                                            <RiskPanel
                                                activeContract={activeContract}
                                                selectedClauseIndex={selectedClauseIndex}
                                                setSelectedClauseIndex={setSelectedClauseIndex}
                                                onDownload={handleDownload}
                                                onInsertRevision={(clause, index) => {
                                                    // 只读模式下不支持插入修订
                                                }}
                                                onScrollToClause={(index) => {
                                                    setTimeout(() => {
                                                        documentViewerRef.current?.scrollToClause(index);
                                                    }, 100);
                                                }}
                                                isAnalyzing={analysisState.isAnalyzing}
                                                analysisProgress={analysisState.progress}
                                            />
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* Workbench Mode: 工作台视图 - 显示上传文件UI */
                                <div className="h-full w-full flex items-center justify-center p-8">
                                    <div className="w-full max-w-lg">
                                        <div className="bg-white/70 backdrop-blur-xl rounded-3xl py-14 px-8 shadow-xl flex flex-col items-center text-center border border-white/50">
                                            {/* 文档图标 */}
                                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
                                                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            
                                            <h2 className="text-2xl font-bold text-slate-800 mb-2">
                                                {t('wb.upload') || '上传并开始审查'}
                                            </h2>
                                            <p className="text-sm text-slate-500 mb-8">
                                                {t('wb.support') || '点击选择或拖放 .docx 合同'}
                                            </p>
                                            
                                            <input
                                                type="file"
                                                className="hidden"
                                                id="workbench-file-upload"
                                                accept=".docx"
                                                onChange={handleFileUpload}
                                            />
                                            <button
                                                onClick={() => document.getElementById('workbench-file-upload')?.click()}
                                                className="px-8 py-3 text-white rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity"
                                                style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
                                            >
                                                {t('wb.drag') || '选择本地文档'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Reveal Path View - 右侧 */}
                        <div className="w-1/2 flex-shrink-0 h-full overflow-hidden">
                            <div className="h-full w-full">
                                <RevealPath
                                    key={`rev-${activeContract?.id ?? 'none'}-${historySelectKey}`}
                                    activeContract={activeContract}
                                    analysisState={analysisState}
                                    viewingHistoryResult={viewingHistoryResult}
                                    onDownload={handleDownload}
                                    onFileUpload={handleFileUpload}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};
